import React from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Link,
  CircularProgress,
} from "@mui/material";
import { ConditionalOutlinedButton } from "../../components/commonComponents/Buttons/ActionButton";
import CyreenLogo from "../../images/cyreen_vertical.png";

// Public, unauthenticated page — linked from the About popup (BottomNav.jsx)
// below Impressum, opened in a new tab. Reads /third-party-notices.json,
// which is generated by oss-policy's check-licenses.sh (ecosystem: node-bundle)
// from build/licenses.json, then placed into public/ by the deploy pipeline
// before the Docker build runs.
const ThirdPartyNotices = () => {
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    fetch("/third-party-notices.json")
      .then((res) => {
        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
        return res.json();
      })
      .then(setData)
      .catch((err) => setError(err.message));
  }, []);

  if (error) {
    return (
      <Box p={4}>
        <Typography variant="h5" gutterBottom>
          Third-Party Notice
        </Typography>
        <Typography color="error">
          Could not load third-party notices ({error}).
        </Typography>
      </Box>
    );
  }

  if (!data) {
    return (
      <Box p={4} display="flex" alignItems="center" gap={2}>
        <CircularProgress size={20} />
        <Typography>Loading third-party notices…</Typography>
      </Box>
    );
  }

  const handleDownload = () => {
    const lines = [
      `${data.product} — Third-Party Notice`,
      `Generated on ${new Date(data.generatedAt).toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" })}`,
      "",
      "Packages",
      "--------",
      ...data.packages.map((pkg) => `${pkg.name} ${pkg.version} — ${pkg.license} — ${pkg.url || "n/a"}`),
      "",
      "Full License Notices",
      "--------------------",
      ...data.licenseTexts.flatMap((group) => [
        group.license,
        `Applies to: ${group.packages.join(", ")}`,
        "",
        group.text,
        "",
      ]),
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${data.product.replace(/\s+/g, "-").toLowerCase()}-third-party-notices.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box p={4} maxWidth={1000} margin="0 auto">
      <Box component="img" src={CyreenLogo} alt="Cyreen" sx={{ width: 140, display: "block", mb: 3 }} />
      <Typography variant="h4" gutterBottom>
        Third-Party Notice
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        CAP Explorer's production browser bundle includes the open-source packages listed below. Full
        license texts and additional notices are provided after the package overview. Generated on{" "}
        {new Date(data.generatedAt).toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" })}.
      </Typography>

      <Box height={24} />

      <Box mb={3}>
        <ConditionalOutlinedButton label="Download full third party notices" onClick={handleDownload} />
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Package</TableCell>
              <TableCell>Version</TableCell>
              <TableCell>License</TableCell>
              <TableCell>URL</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.packages.map((pkg) => (
              <TableRow key={`${pkg.name}@${pkg.version}`}>
                <TableCell>{pkg.name}</TableCell>
                <TableCell>{pkg.version}</TableCell>
                <TableCell>{pkg.license}</TableCell>
                <TableCell>
                  {pkg.url ? (
                    <Link href={pkg.url} target="_blank" rel="noopener noreferrer">
                      {pkg.url}
                    </Link>
                  ) : (
                    "—"
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box height={40} />

      <Typography variant="h5" gutterBottom>
        Full License Notices
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        The following section contains the full license notices for the packages listed above. Each
        distinct notice is shown once.
      </Typography>

      {data.licenseTexts.map((group) => (
        <Box key={group.packages.join(",")} mt={4}>
          <Typography variant="h6">{group.license}</Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Applies to: {group.packages.join(", ")}
          </Typography>
          <Box
            component="pre"
            sx={{
              whiteSpace: "pre-wrap",
              fontFamily: "monospace",
              fontSize: "12px",
              backgroundColor: "rgba(0,0,0,0.03)",
              border: "1px solid rgba(0,0,0,0.1)",
              borderRadius: 1,
              p: 2,
              maxHeight: 400,
              overflow: "auto",
            }}
          >
            {group.text}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default ThirdPartyNotices;